import React, { useState, useEffect, useRef, useCallback } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography as MuiTypography,
  IconButton,
  Card,
  Tab,
  Tabs,
  CircularProgress,
} from "@material-ui/core";
import { useMutation } from "@apollo/client";
import { INVITE_COACH } from "./WorkspaceManagementQueries";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloseIcon from "@material-ui/icons/Close";
import QRCode from "qrcode";

const DialogHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: "1px solid #f0f0f0",
}));

const QRContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  textAlign: "center",
}));

const QRBox = styled(Card)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
}));

const LinkBox = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const InvitationTextField = styled("input")(({ theme }: IStyledArguments) => ({
  flex: 1,
  padding: theme.spacing(1),
  borderRadius: "6px",
  border: "1px solid #e0e0e0",
  fontFamily: "monospace",
  fontSize: "0.85rem",
  backgroundColor: "#f5f5f5",
  cursor: "text",
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
    backgroundColor: "#fff",
  },
}));

const CopyButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(1),
  color: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.extra.accentSoft,
  },
}));

const TabPanel = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3),
}));

const StyledTabs = styled(Tabs)(() => ({
  borderBottom: "1px solid #f0f0f0",
}));

interface IInviteCoachModal {
  open: boolean;
  workspaceId: string;
  onClose: () => void;
  coachName?: string;
  coachId?: string;
}

export default function InviteCoachModal({
  open,
  workspaceId,
  onClose,
}: IInviteCoachModal) {
  const [tabValue, setTabValue] = useState(0);
  const [invitationUrl, setInvitationUrl] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const hasGeneratedRef = useRef(false);

  const [inviteCoach, { loading }] = useMutation(INVITE_COACH, {
    onCompleted: (data) => {
      console.log("✅ Invitation response:", data);
      if (data.inviteCoachToWorkspace?.invitationUrl) {
        console.log("📍 Using backend URL:", data.inviteCoachToWorkspace.invitationUrl);
        setInvitationUrl(data.inviteCoachToWorkspace.invitationUrl);
        setErrorMessage("");
      } else if (data.inviteCoachToWorkspace?.invitationToken) {
        // Build URL from token if URL is not provided
        const token = data.inviteCoachToWorkspace.invitationToken;
        const url = `${window.location.origin}/register-coach?token=${token}`;
        console.log("📍 Built URL from token:", url);
        setInvitationUrl(url);
        setErrorMessage("");
      } else {
        console.error("❌ No invitation URL or token in response");
        setErrorMessage("Backend not ready. Using temporary invitation link...");
        generateTemporaryUrl();
      }
    },
    onError: (error) => {
      console.error("❌ Error generating invitation:", error);
      console.error("Error details:", error.message, error.graphQLErrors);
      setErrorMessage("Backend not ready. Using temporary invitation link...");
      generateTemporaryUrl();
    },
  });

  const generateTemporaryUrl = useCallback(() => {
    // Temporary approach until backend implements inviteCoachToWorkspace
    // Generate a simple URL with workspace ID using production domain
    const url = `https://pro.footik.app/register-coach?workspace=${workspaceId}`;
    setInvitationUrl(url);
    console.log("⚠️ Generated temporary/fallback URL:", url);
  }, [workspaceId]);

  // Generate invitation URL when modal opens
  useEffect(() => {
    if (open && !hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      console.log("Generate Invitation on modal open! WorkspaceId:", workspaceId);
      setErrorMessage("");
      
      // Try backend first
      inviteCoach({
        variables: {
          workspaceId,
        },
      }).catch((error) => {
        // Fallback is already handled in onError
        console.log("Using fallback URL generation", error);
      });
    }
    
    // Reset when modal closes
    if (!open) {
      hasGeneratedRef.current = false;
      setInvitationUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workspaceId]);

  // Generate QR code when URL is available
  useEffect(() => {
    if (invitationUrl && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, invitationUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      }).catch((err) => {
        console.error("Error generating QR code:", err);
      });
    }
  }, [invitationUrl]);

  const handleCopyLink = () => {
    const textInput = document.getElementById("invitation-link") as HTMLInputElement;
    if (textInput) {
      textInput.select();
      document.execCommand("copy");
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleTabChange = (_event: any, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setInvitationUrl("");
    setTabValue(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <MuiTypography variant="h6">
          Invite Coach to Workspace
        </MuiTypography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <DialogContent>
        {loading && !invitationUrl ? (
          <Box py={5} textAlign="center">
            <CircularProgress />
            <MuiTypography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              Generating invitation...
            </MuiTypography>
          </Box>
        ) : !invitationUrl ? (
          <Box py={3} textAlign="center">
            <MuiTypography variant="body1" color="textSecondary" gutterBottom>
              Click "Generate Invitation" to create a link for the coach.
            </MuiTypography>
            <MuiTypography variant="body2" color="textSecondary">
              The coach will be able to create their password and automatically join the workspace.
            </MuiTypography>
            {errorMessage && (
              <MuiTypography variant="body2" style={{ marginTop: 16, color: "#ff9800" }}>
                {errorMessage}
              </MuiTypography>
            )}
          </Box>
        ) : (
          <Box>
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="invitation methods"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="QR Code" />
              <Tab label="Link" />
            </StyledTabs>

            {tabValue === 0 && (
              <TabPanel>
                <QRContainer>
                  <MuiTypography variant="body2" color="textSecondary">
                    Scan with a smartphone camera to register
                  </MuiTypography>
                  <QRBox>
                    <Box
                      style={{
                        padding: "20px",
                        backgroundColor: "#fff",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <canvas ref={qrCanvasRef} />
                    </Box>
                  </QRBox>
                  <MuiTypography variant="caption" color="textSecondary">
                    The coach can scan this code to start the registration process
                  </MuiTypography>
                </QRContainer>
              </TabPanel>
            )}

            {tabValue === 1 && (
              <TabPanel>
                <Box>
                  <MuiTypography variant="body2" gutterBottom style={{ fontWeight: 600 }}>
                    Invitation Link
                  </MuiTypography>
                  <LinkBox>
                    <InvitationTextField
                      id="invitation-link"
                      type="text"
                      value={invitationUrl}
                      readOnly
                    />
                    <CopyButton
                      size="small"
                      onClick={handleCopyLink}
                      title="Copy link"
                    >
                      <FileCopyIcon />
                    </CopyButton>
                  </LinkBox>
                  {copySuccess && (
                    <MuiTypography
                      variant="caption"
                      color="primary"
                      style={{ display: "block", marginTop: 8 }}
                    >
                      Link copied to clipboard!
                    </MuiTypography>
                  )}
                  <MuiTypography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 16 }}>
                    Share this link with the coach via WhatsApp, email, or text message.
                  </MuiTypography>
                </Box>
              </TabPanel>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
